import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { DesktopOnlyModal } from "../../components/DesktopOnlyModal";

/**
 * Ruta de layout del Estudio de marketing. No dibuja chrome: existe para
 * mantener montado el contexto compartido entre las cinco pantallas (si cada
 * una montara el suyo, cambiar de pestaña volvería a pedir todo).
 *
 * Mismo patrón y misma razón que `EstiloMinimal`
 * (ADR-2026-07-31-ruta-layout-shell-sidebar-deja).
 *
 * También es la ÚNICA compuerta de acceso de la sección: al ser la ruta padre
 * de las seis pantallas, el guard de dev tester se escribe una vez acá y las
 * cubre a todas (ADR-2026-08-16-estudio-marketing-guard-dev-tester).
 */

interface EstudioMarketingCtx {
  /** En el Bloque A casi todo es ejemplo. Un solo lugar lo decide. */
  modoEjemplo: boolean;
}

const Ctx = createContext<EstudioMarketingCtx>({ modoEjemplo: true });

export const useEstudioMarketing = () => useContext(Ctx);

export const EstudioMarketingLayout = () => {
  const navigate = useNavigate();

  // `null` = todavía no se sabe. Nunca se renderiza la sección con este valor:
  // sólo `true` abre la puerta, así cualquier otro desenlace (consulta que
  // falla, perfil sin empresa, sesión rara) termina en denegado.
  const [permitido, setPermitido] = useState<boolean | null>(null);

  // Desktop only por ahora: en mobile se avisa y no se dibuja la pantalla.
  // El estado se decide en JS (no con un wrapper `md:hidden`): `DesktopOnlyModal`
  // hace `createPortal` a `document.body` (`DesktopOnlyModal.tsx`), así que sale
  // del subárbol DOM del padre y un wrapper con `display:none` no lo alcanza.
  // Mismo patrón que `Modos.tsx` y `GeneratedImagesHistory.tsx`: umbral de
  // 767px, calculado antes del primer render.
  const [avisoMobile, setAvisoMobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches,
  );

  // Compuerta de acceso: sólo cuentas con `company_profiles.dev_tester = true`.
  // Mismo patrón que `TestV1.tsx`. `ProtectedRoute` sólo verifica que haya
  // sesión, así que sin esto cualquier cliente logueado que escribiera la URL
  // entraba a las seis pantallas (incluida Marca, que escribe `brand_profiles`).
  //
  // FALLA CERRADA a propósito: ante error, sin sesión, sin empresa o con la
  // consulta devolviendo nada, `permitido` queda en `false` y se redirige. No
  // hay rama que conceda acceso por defecto.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          if (vivo) setPermitido(false);
          return;
        }

        const { data: perfil } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = perfil?.id_company ?? null;
        if (!idCompany) {
          if (vivo) setPermitido(false);
          return;
        }

        const { data: empresa } = await supabase
          .from("company_profiles")
          .select("dev_tester")
          .eq("id_company", idCompany)
          .single();
        if (vivo) setPermitido(empresa?.dev_tester === true);
      } catch {
        if (vivo) setPermitido(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (permitido === false) navigate("/", { replace: true });
  }, [permitido, navigate]);

  // Mientras se determina (`null`) no se dibuja NADA: ni las pantallas ni el
  // aviso de mobile. Un cliente que no es dev tester no llega a ver un frame
  // de la sección antes del redirect.
  if (permitido !== true) return null;

  return (
    <Ctx.Provider value={{ modoEjemplo: true }}>
      {/* `md:flex md:min-h-full md:flex-col` y no `md:block`: el panel de
          contenido de la app tiene alto definido (`fixed top-0 bottom-0`), pero
          este envoltorio no lo heredaba, así que cualquier `min-h-full` de una
          pantalla de adentro resolvía contra `auto` y no hacía nada. Sin esto
          la Puesta en marcha no podía centrar sus tarjetas ni mandar el botón
          al fondo. Es un MÍNIMO: las pantallas más largas siguen scrolleando
          igual. */}
      <div className="hidden md:flex md:min-h-full md:flex-col">
        <Outlet />
      </div>
      <DesktopOnlyModal
        open={avisoMobile}
        onClose={() => setAvisoMobile(false)}
        title="Entrá desde la computadora"
        message="El Estudio de marketing todavía funciona solo en pantallas grandes."
      />
    </Ctx.Provider>
  );
};

export default EstudioMarketingLayout;
